/**
 * @typedef {import('vue').ComponentOptions<Vue>} VueComponent
 */

/** @type {VueComponent} */
import Vue from 'vue';
import { getSwiper, getSwiperModules } from '../utils';

export default {
    props: {
        assets: {
            type: Array,
        },
    },

    data() {
        return {
            numberOfSlides: this.assets.length,
            timeout: null,
            currentSlide: 1,
            isDarkVariant: false,
            swiper: null,
        };
    },

    async mounted() {
        if ('isDarkVariant' in this.$parent) {
            this.isDarkVariant = this.$parent.isDarkVariant;
        }
        // This timeout has to be here because of the race condition between the
        // mounted lifecycle hook and the assets prop being passed down.
        this.timeout = setTimeout(async () => {
            await this.initSwiper();
        }, 400);
    },

    destroyed() {
        if (!this.timeout) return;
        clearTimeout(this.timeout);
    },

    watch: {
        async assets(newAssets) {
            this.numberOfSlides = newAssets.length;
            await this.$nextTick(); // Wait for DOM to update with new slides
            await this.initSwiper();
        },
    },

    methods: {
        getContainerClass() {
            return this.numberOfSlides === 1
                ? 'slider-section-carousel__slide--single'
                : '';
        },

        getAssetType(asset) {
            if (asset.video) return 'video';
            if (asset.youtubeVideoUrl) return 'youtubeVideo';
            return 'image';
        },

        async initSwiper() {
            if (this.swiper) {
                // Update the existing swiper instance
                this.swiper.updateSlides();
                this.swiper.updateSize();
                this.swiper.updateProgress();
                this.swiper.updateSlidesClasses();

                // Force scrollbar update
                this.swiper.scrollbar.updateSize();
                this.swiper.scrollbar.init();
                this.swiper.scrollbar.setTranslate();

                // Force navigation update
                if (this.swiper.navigation) {
                    this.swiper.navigation.update();
                }
            } else {
                const { Swiper } = await getSwiper();
                const { Scrollbar, Navigation } = await getSwiperModules();

                const slider = this.$el;
                const isDesktop = window.screen.width >= 768;

                const swiper = new Swiper(slider, {
                    slidesPerView:
                        this.isDarkVariant &&
                        isDesktop &&
                        this.assets.length > 1
                            ? 1.1
                            : 1,
                    spaceBetween: isDesktop ? 40 : 0,
                    centeredSlides: false,
                    loop: false,
                    observer: true,
                    observeParents: true,
                    modules: [Scrollbar, Navigation],
                    scrollbar: {
                        el: '.swiper-scrollbar',
                        hide: false,
                        draggable: true,
                    },
                    uniqueNavElements: true,
                    navigation: {
                        nextEl: '.swiper-button-next',
                        prevEl: '.swiper-button-prev',
                    },
                });

                // Create new swiper instance
                this.swiper = swiper;

                // Add a slight delay to allow DOM to render properly before updating scrollbar
                setTimeout(() => {
                    if (this.swiper && this.swiper.scrollbar) {
                        this.swiper.scrollbar.updateSize();
                        this.swiper.scrollbar.setTranslate();
                    }
                }, 100);
            }
        },

        getSliderSectionAssetClass() {
            return this.isDarkVariant
                ? 'slider-section-carousel__image--desktop--dark'
                : 'slider-section-carousel__image--desktop';
        },
    },
};
